import { AgreeToTermsPolicy, SignInIdentifier } from '@logto/schemas';
import classNames from 'classnames';
import { useCallback, useContext, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import PageContext from '@/Providers/PageContextProvider/PageContext';
import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import WebAuthnContext from '@/Providers/WebAuthnContextProvider/WebAuthnContext';
import LockIcon from '@/assets/icons/lock.svg?react';
import {
  SmartInputField,
  PasswordInputField,
  StudentIdentifierField,
} from '@/components/InputFields';
import CaptchaBox from '@/containers/CaptchaBox';
import ForgotPasswordLink from '@/containers/ForgotPasswordLink';
import TermsAndPrivacyCheckbox from '@/containers/TermsAndPrivacyCheckbox';
import { useIsAimsLayout } from '@/hooks/use-layout-mode';
import usePasswordSignIn from '@/hooks/use-password-sign-in';
import usePrefilledIdentifier from '@/hooks/use-prefilled-identifier';
import { useForgotPasswordSettings } from '@/hooks/use-sie';
import useSingleSignOnWatch from '@/hooks/use-single-sign-on-watch';
import { useStudentLogin } from '@/hooks/use-student-login';
import useTerms from '@/hooks/use-terms';
import Button from '@/shared/components/Button';
import ErrorMessage from '@/shared/components/ErrorMessage';
import type { IdentifierInputValue } from '@/shared/components/InputFields/SmartInputField';
import { getGeneralIdentifierErrorMessage, validateIdentifierField } from '@/utils/form';

import styles from './index.module.scss';

type Props = {
  readonly className?: string;
  // eslint-disable-next-line react/boolean-prop-naming
  readonly autoFocus?: boolean;
  readonly signInMethods: SignInIdentifier[];
  /** Applicants: "Send me a code instead" switches the page to a code sign-in. */
  readonly onUseCode?: () => void;
};

export type FormState = {
  identifier: IdentifierInputValue;
  password: string;
};

const PasswordSignInForm = ({ className, autoFocus, signInMethods, onUseCode }: Props) => {
  const { t } = useTranslation();

  const { errorMessage, clearErrorMessage, onSubmit } = usePasswordSignIn();
  const { isForgotPasswordEnabled } = useForgotPasswordSettings();
  const { termsValidation, agreeToTermsPolicy } = useTerms();
  const { setIdentifierInputValue } = useContext(UserInteractionContext);
  const { isPasskeyFlowProcessing } = useContext(WebAuthnContext);
  const { isStudentLoginEnabled, isStudentLookupEnabled } = useStudentLogin();
  // The AIMS layout speaks to staff: "Staff number or email", then "Continue". The label is left
  // alone when phone sign-in is on, which needs the country-code field the override would drop.
  const isAimsLayout = useIsAimsLayout();
  const { experienceSettings } = useContext(PageContext);
  // Applicants read "Email or phone number" and "Sign in" (admissions sign-in mockup).
  const isApplicant = isAimsLayout && experienceSettings?.branding.aimsAudience === 'applicant';
  const identifierLabel = isApplicant
    ? t('description.aims_layout.applicant_identifier')
    : isAimsLayout && !signInMethods.includes(SignInIdentifier.Phone)
      ? t('description.aims_layout.identifier')
      : undefined;
  const signInTitle = isAimsLayout && !isApplicant ? 'action.continue' : 'action.sign_in';
  const prefilledIdentifier = usePrefilledIdentifier({ enabledIdentifiers: signInMethods });

  const {
    watch,
    register,
    handleSubmit,
    control,
    formState: { errors, isValid, isSubmitting },
  } = useForm<FormState>({
    reValidateMode: 'onBlur',
    defaultValues: {
      identifier: prefilledIdentifier,
      password: '',
    },
  });

  const { showSingleSignOnForm, navigateToSingleSignOn } = useSingleSignOnWatch(
    watch('identifier')
  );

  const onSubmitHandler = useCallback(
    async (event?: React.FormEvent<HTMLFormElement>) => {
      if (isPasskeyFlowProcessing) {
        return;
      }

      clearErrorMessage();

      await handleSubmit(async ({ identifier: { type, value }, password }) => {
        if (!type) {
          return;
        }

        setIdentifierInputValue({ type, value });

        if (showSingleSignOnForm) {
          await navigateToSingleSignOn();
          return;
        }

        // Check if the user has agreed to the terms and privacy policy before signing in when the policy is set to `Manual`
        if (agreeToTermsPolicy === AgreeToTermsPolicy.Manual && !(await termsValidation())) {
          return;
        }

        await onSubmit({
          identifier: { type, value },
          password,
        });
      })(event);
    },
    [
      agreeToTermsPolicy,
      clearErrorMessage,
      handleSubmit,
      navigateToSingleSignOn,
      onSubmit,
      setIdentifierInputValue,
      showSingleSignOnForm,
      termsValidation,
      isPasskeyFlowProcessing,
    ]
  );

  useEffect(() => {
    if (!isValid) {
      clearErrorMessage();
    }
  }, [clearErrorMessage, isValid]);

  return (
    <form className={classNames(styles.form, className)} onSubmit={onSubmitHandler}>
      <Controller
        control={control}
        name="identifier"
        rules={{
          validate: ({ type, value }) => {
            if (!type || !value) {
              return getGeneralIdentifierErrorMessage(signInMethods, 'required');
            }

            // Student-identifier mode submits a login alias (student / registration number),
            // not a Logto username. Aliases may start with a digit or contain `/`, so the
            // username hard-floor must not apply; the server's alias resolver decides validity.
            if (isStudentLoginEnabled) {
              return true;
            }

            const errorMessage = validateIdentifierField(type, value);

            return errorMessage ? getGeneralIdentifierErrorMessage(signInMethods, 'invalid') : true;
          },
        }}
        render={({ field, formState: { defaultValues } }) =>
          isStudentLoginEnabled ? (
            <StudentIdentifierField
              autoFocus={autoFocus}
              className={styles.inputField}
              {...field}
              isDanger={!!errors.identifier}
              errorMessage={errors.identifier?.message}
              defaultValue={defaultValues?.identifier?.value}
            />
          ) : (
            <SmartInputField
              autoFocus={autoFocus}
              className={styles.inputField}
              {...field}
              isDanger={!!errors.identifier}
              errorMessage={errors.identifier?.message}
              enabledTypes={signInMethods}
              labelOverride={identifierLabel}
              placeholder={
                isApplicant
                  ? t('description.aims_layout.applicant_identifier_placeholder')
                  : undefined
              }
              defaultValue={defaultValues?.identifier?.value}
            />
          )
        }
      />
      {showSingleSignOnForm && (
        <div className={styles.message}>{t('description.single_sign_on_enabled')}</div>
      )}

      {!showSingleSignOnForm && (
        <PasswordInputField
          className={styles.inputField}
          autoComplete="current-password"
          label={t('input.password')}
          isDanger={!!errors.password}
          errorMessage={errors.password?.message}
          {...register('password', { required: t('error.password_required') })}
        />
      )}

      {errorMessage && <ErrorMessage className={styles.formErrors}>{errorMessage}</ErrorMessage>}

      {/**
       * In student mode the reset channel is derived from the resolved account, not from a
       * tenant email/phone sign-in identifier, so the link must show even when the tenant's
       * email/phone forgot-password methods are disabled.
       */}
      {(isForgotPasswordEnabled || isStudentLookupEnabled) && !showSingleSignOnForm && (
        <ForgotPasswordLink
          className={styles.link}
          identifier={watch('identifier').type}
          value={watch('identifier').value}
        />
      )}

      {/**
       * Have to use css to hide the terms element.
       * Remove element from dom will trigger a form re-render.
       * Form rerender will trigger autofill.
       * If the autofill value is SSO enabled, it will always show SSO form.
       */}
      <TermsAndPrivacyCheckbox
        className={classNames(
          styles.terms,
          // For sign in, only show the terms checkbox if the terms policy is manual
          agreeToTermsPolicy !== AgreeToTermsPolicy.Manual && styles.hidden
        )}
      />

      <CaptchaBox />

      <Button
        name="submit"
        title={showSingleSignOnForm ? 'action.single_sign_on' : signInTitle}
        icon={showSingleSignOnForm ? <LockIcon /> : undefined}
        htmlType="submit"
        isLoading={isSubmitting || isPasskeyFlowProcessing}
      />
      {onUseCode && (
        <Button
          className={styles.useCode}
          type="secondary"
          title="description.aims_layout.send_code_instead"
          onClick={onUseCode}
        />
      )}

      <input hidden type="submit" />
    </form>
  );
};

export default PasswordSignInForm;
